import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { ChevronDown, ChevronRight, HelpCircle, Info } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useFinancialData } from '../../contexts/FinancialDataContext';
import { useAuth } from '../../contexts/AuthContext';
import { formatCurrency, formatCompactDollars as formatCompact } from '../../utils/formatters';
import { saveRetirementSettings } from '../../services/googleSheetsService';
import { Tooltip } from '../shared';
import { TIER_LIMITS } from '../../config/constants';
import { projectRetirement } from '../../utils/calculators';

type AssetClass = 'stocks' | 'crypto' | '401k' | 'cash' | 'pe';

interface ProjectedHolding {
  key: string;
  name: string;
  ticker: string;
  assetClass: AssetClass;
  currentPrice: number;
  quantity: number;
  totalValue: number;
  cagr: number;
}

interface RetirementProjectionProps {
  variant?: 'card' | 'page';
  onUpgradeClick?: () => void;
}

const validBirthYear = (value: string): number | null => {
  const year = Number(value);
  return year >= 1900 && year <= new Date().getFullYear() ? year : null;
};

export const RetirementProjection: React.FC<RetirementProjectionProps> = ({ onUpgradeClick }) => {
  const { darkMode, privacyMode } = useTheme();
  const {
    investments, retirement401k, cryptoHoldings, bankingData,
    privateEquityHoldings,
    retirementSettings,
    updateRetirementSettings,
    totalSpent,
  } = useFinancialData();
  const { sheetId, paymentTier } = useAuth();
  const isPro = paymentTier === 'pro';

  const currentYear = new Date().getFullYear();

  // Settings state — initialized from saved settings or defaults
  const [retirementYearStr, setRetirementYearStr] = useState(() =>
    retirementSettings ? String(retirementSettings.retirementYear) : String(currentYear + 20)
  );
  const [annualSpendingStr, setAnnualSpendingStr] = useState(() =>
    retirementSettings ? String(retirementSettings.annualSpending) : (totalSpent > 0 ? String(Math.round(totalSpent * 12)) : '80000')
  );
  const [inflationRateStr, setInflationRateStr] = useState(() =>
    retirementSettings ? String(retirementSettings.inflationRate) : '3'
  );
  const [cagrOverrides, setCagrOverrides] = useState<Record<string, string>>(() => {
    if (retirementSettings?.cagrOverrides) {
      try { return JSON.parse(retirementSettings.cagrOverrides); } catch { return {}; }
    }
    return {};
  });
  const [taxRateStr, setTaxRateStr] = useState(() =>
    retirementSettings ? String(retirementSettings.taxRate) : '0'
  );
  const [birthYearStr, setBirthYearStr] = useState(() =>
    retirementSettings?.birthYear ? String(retirementSettings.birthYear) : ''
  );
  // Draft while the age box is being typed in; otherwise age is derived from the year.
  const [ageDraft, setAgeDraft] = useState<string | null>(null);
  const [enabledClasses, setEnabledClasses] = useState<Set<AssetClass>>(() => {
    if (retirementSettings?.enabledClasses) {
      return new Set<AssetClass>(retirementSettings.enabledClasses.split(',').filter(Boolean) as AssetClass[]);
    }
    return new Set<AssetClass>(['stocks', 'crypto', '401k', 'pe']);
  });

  // Auto-save settings to Google Sheet (debounced)
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!sheetId) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      const settings = {
        retirementYear: Number(retirementYearStr) || currentYear + 20,
        annualSpending: Number(annualSpendingStr) || 0,
        inflationRate: Number(inflationRateStr) || 0,
        growthOutlook: 'baseline',
        enabledClasses: Array.from(enabledClasses).join(','),
        conservativeBp: 0,
        optimisticBp: 0,
        cagrOverrides: JSON.stringify(cagrOverrides),
        taxRate: Number(taxRateStr) || 0,
        birthYear: validBirthYear(birthYearStr),
      };
      updateRetirementSettings(settings);
      saveRetirementSettings(sheetId, settings)
        .catch(err => console.warn('Failed to save retirement settings:', err));
    }, 1500);
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
  }, [sheetId, retirementYearStr, annualSpendingStr, inflationRateStr, taxRateStr, birthYearStr, enabledClasses, currentYear, cagrOverrides, updateRetirementSettings]);

  const retirementYear = Number(retirementYearStr) || currentYear + 20;
  const annualSpending = Number(annualSpendingStr) || 0;
  const inflationRate = Number(inflationRateStr) || 0;
  const taxRate = Number(taxRateStr) || 0;
  const [holdingsOpen, setHoldingsOpen] = useState(false);
  const [breakdownOpen, setBreakdownOpen] = useState(false);
  const [tutorialOpen, setTutorialOpen] = useState(false);

  const toggleAssetClass = useCallback((ac: AssetClass) => {
    setEnabledClasses(prev => {
      const next = new Set(prev);
      if (next.has(ac)) next.delete(ac);
      else next.add(ac);
      return next;
    });
  }, []);

  // Build holdings list from real data
  const holdings: ProjectedHolding[] = useMemo(() => {
    const result: ProjectedHolding[] = [];

    const getCagr = (key: string) => {
      const override = cagrOverrides[key];
      return override !== undefined ? (Number(override) || 0) : 0;
    };

    for (const inv of investments) {
      const key = `stocks-${inv.symbol}`;
      result.push({
        key, name: inv.symbol, ticker: inv.symbol, assetClass: 'stocks',
        currentPrice: inv.currentPrice, quantity: inv.shares,
        totalValue: inv.shares * inv.currentPrice,
        cagr: getCagr(key),
      });
    }

    for (const h of retirement401k) {
      const key = `401k-${h.ticker || h.fundName}`;
      result.push({
        key, name: h.fundName, ticker: h.ticker || '', assetClass: '401k',
        currentPrice: h.shares > 0 ? h.balance / h.shares : h.balance,
        quantity: h.shares || 1, totalValue: h.balance,
        cagr: getCagr(key),
      });
    }

    for (const c of cryptoHoldings) {
      const key = `crypto-${c.symbol}`;
      result.push({
        key, name: c.symbol, ticker: c.symbol, assetClass: 'crypto',
        currentPrice: c.currentPrice, quantity: c.amount,
        totalValue: c.amount * c.currentPrice,
        cagr: getCagr(key),
      });
    }

    for (const pe of privateEquityHoldings) {
      const key = `pe-${pe.symbol || pe.name}`;
      result.push({
        key, name: pe.name, ticker: pe.symbol || '', assetClass: 'pe',
        currentPrice: pe.pricePerShare, quantity: pe.shares,
        totalValue: pe.totalValue,
        cagr: getCagr(key),
      });
    }

    for (const b of bankingData) {
      const key = `cash-${b.name}`;
      result.push({
        key, name: b.name, ticker: '', assetClass: 'cash',
        currentPrice: b.balance, quantity: 1, totalValue: b.balance,
        cagr: getCagr(key),
      });
    }

    return result.filter(h => enabledClasses.has(h.assetClass));
  }, [investments, retirement401k, cryptoHoldings, privateEquityHoldings, bankingData, cagrOverrides, enabledClasses]);

  const portfolioTotal = holdings.reduce((sum, h) => sum + h.totalValue, 0);
  const { rows, portfolioAtRetirement, depletionYear, yearsUntilRetirement } = useMemo(
    () => projectRetirement({ holdings, currentYear, retirementYear, annualSpending, inflationRate, taxRate }),
    [holdings, retirementYear, annualSpending, inflationRate, taxRate, currentYear],
  );

  // Chart dimensions
  // Responsive container width detection
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 800);
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    setContainerWidth(el.clientWidth);
    const ro = new ResizeObserver(entries => {
      for (const entry of entries) setContainerWidth(entry.contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Breakpoints: mobile <640, tablet <1024, desktop >=1024
  const isMobile = containerWidth < 640;
  const isTablet = containerWidth >= 640 && containerWidth < 1024;

  // Chart dimensions — padding scales for larger labels on small screens
  const chartW = 900;
  const chartH = 300;
  const padL = isMobile ? 110 : isTablet ? 70 : 50;
  const padR = isMobile ? 15 : isTablet ? 15 : 10;
  const padT = isMobile ? 30 : 20;
  const padB = isMobile ? 50 : 35;
  const plotW = chartW - padL - padR;
  const plotH = chartH - padT - padB;

  // Responsive tick & label sizing
  const xTickInterval = isMobile ? 10 : isTablet ? 5 : 2;
  const yTickCount = isMobile ? 4 : isTablet ? 6 : 8;
  const labelFontSize = isMobile ? 18 : isTablet ? 12 : 9;
  const retireLabelFontSize = isMobile ? 16 : isTablet ? 10 : 7;

  // Depth gate. The projection always runs across the full 50 years — the
  // headline cards above are computed from `rows` and stay free. Only the
  // charted horizon is truncated, so a free user sees a real trajectory on
  // their real data, just a shorter one.
  const projectionYears = TIER_LIMITS[paymentTier].projectionYears;
  const visibleRows = Number.isFinite(projectionYears) ? rows.slice(0, projectionYears) : rows;
  const horizonTruncated = visibleRows.length < rows.length;
  const lastVisibleYear = visibleRows.length ? visibleRows[visibleRows.length - 1].year : currentYear;
  // Retirement can fall outside a truncated window; don't draw a marker off-plot.
  const retirementInView = yearsUntilRetirement < visibleRows.length;

  // Include portfolioAtRetirement (pre-spending, so it can exceed closingBalance
  // peaks) only when the retirement year is actually on the chart — otherwise it
  // would scale the axis to a number the visible curve never reaches.
  const maxVal = Math.max(
    ...visibleRows.map(r => r.closingBalance),
    retirementInView ? portfolioAtRetirement : 0,
    1
  );
  const retirementX = visibleRows.length > 1
    ? padL + (yearsUntilRetirement / (visibleRows.length - 1)) * plotW
    : padL;

  const chartPoints = visibleRows.map((r, i) => {
    const x = padL + (i / Math.max(visibleRows.length - 1, 1)) * plotW;
    const y = padT + plotH - (r.closingBalance / maxVal) * plotH;
    return `${x},${y}`;
  }).join(' ');

  const areaPoints = chartPoints + ` ${padL + plotW},${padT + plotH} ${padL},${padT + plotH}`;

  const yTicks = Array.from({ length: yTickCount + 1 }, (_, i) => i / yTickCount).map(pct => ({
    val: maxVal * pct,
    y: padT + plotH - pct * plotH,
  }));

  // X-axis ticks — evenly spaced at interval, no forced first/last
  const xTicks = visibleRows.length > 1
    ? visibleRows
        .map((r, i) => ({ year: r.year, x: padL + (i / (visibleRows.length - 1)) * plotW }))
        .filter(t => t.year % xTickInterval === 0)
    : [];

  // Consolidate holdings by name within each asset class
  interface ConsolidatedHolding {
    name: string;
    assetClass: AssetClass;
    totalValue: number;
    cagr: number;
    keys: string[];
  }

  const consolidatedByClass = useMemo(() => {
    const groups: Record<AssetClass, ConsolidatedHolding[]> = { stocks: [], crypto: [], '401k': [], cash: [], pe: [] };
    const map = new Map<string, ConsolidatedHolding>();
    for (const h of holdings) {
      const consolidateKey = `${h.assetClass}-${h.name}`;
      const existing = map.get(consolidateKey);
      if (existing) {
        existing.totalValue += h.totalValue;
        existing.keys.push(h.key);
      } else {
        const entry: ConsolidatedHolding = {
          name: h.name, assetClass: h.assetClass, totalValue: h.totalValue,
          cagr: h.cagr, keys: [h.key],
        };
        map.set(consolidateKey, entry);
        groups[h.assetClass].push(entry);
      }
    }
    return groups;
  }, [holdings]);

  const handleConsolidatedCagrChange = useCallback((keys: string[], value: string) => {
    setCagrOverrides(prev => {
      const next = { ...prev };
      for (const k of keys) next[k] = value;
      return next;
    });
  }, []);

  const groupLabels: Record<AssetClass, string> = { stocks: 'Stocks', crypto: 'Crypto', '401k': '401(k)', cash: 'Cash', pe: 'Private Equity' };

  const inputClass = `w-full px-3 py-2 rounded-lg text-sm ${
    darkMode
      ? 'bg-white/10 border border-white/10 text-white placeholder-slate-500'
      : 'bg-slate-50 border border-slate-200 text-slate-900 placeholder-slate-400'
  }`;

  const labelClass = `text-xs font-medium ${darkMode ? 'text-slate-400' : 'text-slate-500'}`;
  const cardBg = darkMode ? 'bg-white/5 border border-white/10' : 'bg-slate-50 border border-slate-200';
  const settingsCardClass = `rounded-xl p-4 flex flex-col justify-start ${cardBg}`;

  const yearsMoneyLasts = depletionYear ? Math.max(depletionYear - retirementYear, 0) : null;

  const sectionToggleClass = `flex items-center gap-2 w-full py-2.5 text-left text-sm font-semibold transition-colors ${
    darkMode ? 'text-slate-300 hover:text-white' : 'text-slate-700 hover:text-slate-900'
  }`;

  return (
    <div ref={containerRef}>
      <div className="flex items-center gap-2 mb-4">
        <div className="flex flex-wrap gap-2 flex-1">
          {(['stocks', 'crypto', '401k', 'pe', 'cash'] as AssetClass[]).map(ac => {
            const active = enabledClasses.has(ac);
            return (
              <button
                key={ac}
                onClick={() => toggleAssetClass(ac)}
                className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors ${
                  active
                    ? (darkMode ? 'bg-violet-500/30 text-violet-300 border border-violet-500/40' : 'bg-violet-100 text-violet-700 border border-violet-200')
                    : (darkMode ? 'bg-white/5 text-slate-500 border border-white/10 line-through' : 'bg-slate-100 text-slate-400 border border-slate-200 line-through')
                }`}
              >
                {groupLabels[ac]}
              </button>
          );
          })}
        </div>
        <button
          onClick={() => setTutorialOpen(true)}
          className={`p-1.5 rounded-lg transition-colors flex-shrink-0 ${
            darkMode ? 'hover:bg-white/10 text-slate-400' : 'hover:bg-slate-200 text-slate-500'
          }`}
          title="How to use this tool"
        >
          <HelpCircle size={18} />
        </button>
      </div>

      {/* Tutorial Dialog */}
      {tutorialOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setTutorialOpen(false)}>
          <div
            className={`w-full max-w-md max-h-[85dvh] overflow-y-auto rounded-2xl p-5 sm:p-6 shadow-2xl ${
              darkMode ? 'bg-slate-900 border border-white/10' : 'bg-white border border-slate-200'
            }`}
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                How It Works
              </h3>
              <button
                onClick={() => setTutorialOpen(false)}
                className={`p-1 rounded-lg transition-colors ${darkMode ? 'hover:bg-white/10 text-slate-400' : 'hover:bg-slate-100 text-slate-500'}`}
              >
                <span className="text-lg">&times;</span>
              </button>
            </div>

            <div className={`space-y-4 text-sm ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
              <div>
                <p className={`font-semibold mb-1 ${darkMode ? 'text-white' : 'text-slate-900'}`}>1. Set your inputs</p>
                <p>Enter your target <strong>retirement year</strong>, expected <strong>annual spending</strong>, and <strong>inflation rate</strong> in the settings cards at the top.</p>
              </div>

              <div>
                <p className={`font-semibold mb-1 ${darkMode ? 'text-white' : 'text-slate-900'}`}>2. Choose asset classes</p>
                <p>Use the pills at the top to toggle which asset classes (Stocks, Crypto, 401k, etc.) are included in your projection.</p>
              </div>

              <div>
                <p className={`font-semibold mb-1 ${darkMode ? 'text-white' : 'text-slate-900'}`}>3. Set growth rates per asset</p>
                <p>Expand <strong>Holdings & Growth Rates</strong> to set your estimated annual growth rate (CAGR) for each asset. Use the <strong>+/&minus;</strong> buttons to quickly adjust by 1%.</p>
              </div>

              <div>
                <p className={`font-semibold mb-1 ${darkMode ? 'text-white' : 'text-slate-900'}`}>4. Review your projection</p>
                <p>The chart and summary cards update in real time. Expand <strong>Year-by-Year Breakdown</strong> to see the numbers for every year.</p>
              </div>
            </div>

            <div className="flex justify-end mt-5">
              <button
                onClick={() => setTutorialOpen(false)}
                className="px-5 py-2 rounded-lg text-sm font-medium bg-violet-600 hover:bg-violet-700 text-white transition-colors"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-4">

        {/* Settings Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
          {/* Portfolio Overview */}
          <div className={settingsCardClass}>
            <p className={labelClass}>Portfolio Value</p>
            <p className={`text-lg font-bold mt-1 ${darkMode ? 'text-white' : 'text-slate-900'}`}>
              {privacyMode ? '••••••' : formatCurrency(portfolioTotal)}
            </p>
            <p className={`text-[10px] mt-0.5 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
              {holdings.length} asset{holdings.length !== 1 ? 's' : ''}
            </p>
          </div>

          {/* Retirement Year */}
          <div className={settingsCardClass}>
            <label className={labelClass}>Retirement Year</label>
            <input
              type="text"
              inputMode="numeric"
              value={retirementYearStr}
              onChange={e => setRetirementYearStr(e.target.value.replace(/[^0-9]/g, ''))}
              onFocus={e => e.target.select()}
              className={`${inputClass} mt-1 text-lg font-bold`}
            />
            {(() => {
              const birthYear = validBirthYear(birthYearStr);
              const derivedAge = birthYear && retirementYear ? retirementYear - birthYear : null;
              const smallInput = `w-16 px-2 py-1 rounded text-xs text-right ${
                darkMode
                  ? 'bg-white/10 border border-white/10 text-white'
                  : 'bg-white border border-slate-200 text-slate-900'
              }`;
              const smallLabel = `text-[10px] ${darkMode ? 'text-slate-500' : 'text-slate-400'}`;
              return (
                <>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-2">
                    <label className="flex items-center gap-2 whitespace-nowrap">
                      <span className={smallLabel}>Birth Year</span>
                      <input
                        type="text"
                        inputMode="numeric"
                        maxLength={4}
                        placeholder="1985"
                        value={birthYearStr}
                        onChange={e => setBirthYearStr(e.target.value.replace(/[^0-9]/g, ''))}
                        onFocus={e => e.target.select()}
                        className={smallInput}
                      />
                    </label>
                    <label className="flex items-center gap-2 whitespace-nowrap">
                      <span className={smallLabel}>Retire at</span>
                      <input
                        type="text"
                        inputMode="numeric"
                        maxLength={3}
                        placeholder="65"
                        disabled={!birthYear}
                        value={ageDraft ?? (derivedAge != null ? String(derivedAge) : '')}
                        onFocus={e => { setAgeDraft(derivedAge != null ? String(derivedAge) : ''); e.target.select(); }}
                        onBlur={() => setAgeDraft(null)}
                        onChange={e => {
                          const draft = e.target.value.replace(/[^0-9]/g, '');
                          setAgeDraft(draft);
                          const age = Number(draft);
                          if (birthYear && age >= 18 && age <= 110) setRetirementYearStr(String(birthYear + age));
                        }}
                        className={`${smallInput} disabled:opacity-40`}
                      />
                    </label>
                  </div>
                  <p className={`text-[10px] mt-1.5 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                    {birthYear
                      ? `You're ${currentYear - birthYear} now and would retire at ${derivedAge} in ${retirementYear}${retirementYear > currentYear ? `, ${retirementYear - currentYear} years from now` : ''}.`
                      : 'Add your birth year to set retirement by age.'}
                  </p>
                </>
              );
            })()}
          </div>

          {/* Retirement Settings */}
          <div className={settingsCardClass}>
            <label className={`${labelClass} flex items-center gap-1.5`}>
              Annual Spending
              <Tooltip content="How much you expect to spend each year in retirement. This is what your portfolio needs to cover — it determines your nest egg target and how long your money lasts.">
                <HelpCircle size={12} className={`cursor-help ${darkMode ? 'text-slate-500' : 'text-slate-400'}`} />
              </Tooltip>
            </label>
            <p className={`text-[10px] mt-0.5 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
              Expenses you'll have during retirement
            </p>
            <input
              type="text"
              inputMode="numeric"
              value={annualSpendingStr ? `$${Number(annualSpendingStr).toLocaleString()}` : ''}
              onChange={e => setAnnualSpendingStr(e.target.value.replace(/[^0-9]/g, ''))}
              onFocus={e => e.target.select()}
              className={`${inputClass} mt-1`}
            />
            {/* Each label + input + % wraps as one unit so a label never strands from its box */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-2">
              <label className="flex items-center gap-2 whitespace-nowrap">
                <span className={`text-[10px] ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Inflation</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={inflationRateStr}
                  onChange={e => setInflationRateStr(e.target.value.replace(/[^0-9.]/g, ''))}
                  onFocus={e => e.target.select()}
                  className={`w-16 px-2 py-1 rounded text-xs text-right ${
                    darkMode
                      ? 'bg-white/10 border border-white/10 text-white'
                      : 'bg-white border border-slate-200 text-slate-900'
                  }`}
                />
                <span className={`text-[10px] ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>%</span>
              </label>
              <label className="flex items-center gap-2 whitespace-nowrap">
                <span className={`text-[10px] ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Est. Tax Rate</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={taxRateStr}
                  onChange={e => setTaxRateStr(e.target.value.replace(/[^0-9.]/g, ''))}
                  onFocus={e => e.target.select()}
                  className={`w-16 px-2 py-1 rounded text-xs text-right ${
                    darkMode
                      ? 'bg-white/10 border border-white/10 text-white'
                      : 'bg-white border border-slate-200 text-slate-900'
                  }`}
                />
                <span className={`text-[10px] ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>%</span>
              </label>
            </div>
          </div>

        </div>

        {/* Outlook Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className={`rounded-xl p-4 ${darkMode ? 'bg-violet-500/10 border border-violet-500/20' : 'bg-violet-50 border border-violet-100'}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-xs font-medium flex items-center gap-1 ${darkMode ? 'text-violet-400/70' : 'text-violet-500'}`}>
                  Projected Nest Egg
                  <Tooltip content="Your projected portfolio value at retirement. Calculated by growing each holding by its CAGR until your retirement year, then subtracting any spending for years already in retirement.">
                    <HelpCircle size={11} className={`cursor-help ${darkMode ? 'text-violet-400/50' : 'text-violet-500/70'}`} />
                  </Tooltip>
                </p>
                <p className={`text-lg md:text-xl font-bold mt-1 ${darkMode ? 'text-violet-300' : 'text-violet-700'}`}>
                  {privacyMode ? '••••••' : formatCompact(portfolioAtRetirement)}
                </p>
              </div>
              <span className={`text-[10px] px-2 py-1 rounded-lg ${darkMode ? 'bg-violet-500/20 text-violet-400' : 'bg-violet-100 text-violet-600'}`}>
                {retirementYear}
              </span>
            </div>
          </div>
          <div className={`rounded-xl p-4 ${
            yearsMoneyLasts === null || yearsMoneyLasts > 30
              ? (darkMode ? 'bg-emerald-500/10 border border-emerald-500/20' : 'bg-emerald-50 border border-emerald-100')
              : (darkMode ? 'bg-red-500/10 border border-red-500/20' : 'bg-red-50 border border-red-100')
          }`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-xs font-medium flex items-center gap-1 ${
                  yearsMoneyLasts === null || yearsMoneyLasts > 30
                    ? (darkMode ? 'text-emerald-400/70' : 'text-emerald-500')
                    : (darkMode ? 'text-red-400/70' : 'text-red-500')
                }`}>
                  Runway
                  <Tooltip content={`How many years your portfolio will fund your annual spending in retirement. ${yearsMoneyLasts === null ? 'Sustainable means your investment growth covers withdrawals indefinitely.' : `Based on annual spending growing with inflation, your portfolio runs out in ${depletionYear}.`}`}>
                    <HelpCircle size={11} className={`cursor-help ${darkMode ? 'opacity-60' : 'opacity-70'}`} />
                  </Tooltip>
                </p>
                <p className={`text-lg md:text-xl font-bold mt-1 ${
                  yearsMoneyLasts === null || yearsMoneyLasts > 30
                    ? (darkMode ? 'text-emerald-300' : 'text-emerald-700')
                    : (darkMode ? 'text-red-300' : 'text-red-700')
                }`}>
                  {yearsMoneyLasts === null ? 'Indefinite' : `${yearsMoneyLasts} yrs`}
                </p>
              </div>
              <span className={`text-[10px] px-2 py-1 rounded-lg ${
                yearsMoneyLasts === null || yearsMoneyLasts > 30
                  ? (darkMode ? 'bg-emerald-500/20 text-emerald-400' : 'bg-emerald-100 text-emerald-600')
                  : (darkMode ? 'bg-red-500/20 text-red-400' : 'bg-red-100 text-red-600')
              }`}>
                {yearsMoneyLasts === null ? 'sustainable' : `ends ${depletionYear}`}
              </span>
            </div>
          </div>
          {(() => {
            const yearsToRetire = Math.max(retirementYear - currentYear, 0);
            const inflatedSpending = annualSpending * Math.pow(1 + inflationRate / 100, yearsToRetire);
            return (
              <div className={`rounded-xl p-4 ${darkMode ? 'bg-amber-500/10 border border-amber-500/20' : 'bg-amber-50 border border-amber-100'}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`text-xs font-medium flex items-center gap-1 ${darkMode ? 'text-amber-400/70' : 'text-amber-500'}`}>
                      Spending in Retirement
                      <Tooltip content={`Your Annual Spending (${formatCompact(annualSpending)}) adjusted for ${inflationRate}% inflation over ${yearsToRetire} years. This is what you've told us you'll need each year starting at retirement.`}>
                        <HelpCircle size={11} className={`cursor-help ${darkMode ? 'text-amber-400/50' : 'text-amber-500/70'}`} />
                      </Tooltip>
                    </p>
                    <p className={`text-lg md:text-xl font-bold mt-1 ${darkMode ? 'text-amber-300' : 'text-amber-700'}`}>
                      {privacyMode ? '••••••' : formatCompact(inflatedSpending)}
                    </p>
                    {taxRate > 0 && !privacyMode && (
                      <p className={`text-xs mt-1 ${darkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>
                        Pre-tax need: {formatCompact(inflatedSpending / (1 - taxRate / 100))}
                      </p>
                    )}
                  </div>
                  <span className={`text-[10px] px-2 py-1 rounded-lg block ${darkMode ? 'bg-amber-500/20 text-amber-400' : 'bg-amber-100 text-amber-600'}`}>
                    yr {retirementYear}
                  </span>
                </div>
              </div>
            );
          })()}
        </div>

        {/* Wealth Trajectory */}
        {visibleRows.length > 1 && (
          <div className={`rounded-xl py-3 px-2 ${cardBg}`}>
            <h3 className={`text-xs font-medium uppercase tracking-wider mb-3 px-2 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
              Wealth Trajectory
            </h3>
            <svg viewBox={`0 0 ${chartW} ${chartH}`} className="w-full max-h-[55vh] sm:max-h-[50vh] lg:max-h-[40vh]">
              <defs>
                <linearGradient id="projGrad" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="#14b8a6" stopOpacity="0.25" />
                  <stop offset="100%" stopColor="#a855f7" stopOpacity="0.05" />
                </linearGradient>
                <linearGradient id="lineGrad" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#14b8a6" />
                  <stop offset="100%" stopColor="#a855f7" />
                </linearGradient>
              </defs>

              {yTicks.map((t, i) => (
                <g key={i}>
                  <line
                    x1={padL} y1={t.y} x2={padL + plotW} y2={t.y}
                    stroke={darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}
                  />
                  <line
                    x1={padL - 4} y1={t.y} x2={padL} y2={t.y}
                    stroke={darkMode ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)'}
                  />
                  <text x={padL - 8} y={t.y + 3} textAnchor="end" fill={darkMode ? '#6b7280' : '#9ca3af'} fontSize={labelFontSize}>
                    {formatCompact(t.val, { wholeThousands: true })}
                  </text>
                </g>
              ))}

              <polygon points={areaPoints} fill="url(#projGrad)" />
              <polyline
                points={chartPoints}
                fill="none"
                stroke="url(#lineGrad)"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />

              {retirementInView && (() => {
                // Align retirement dot to the displayed Nest Egg (post-growth, pre-spending value)
                const dotY = padT + plotH - (portfolioAtRetirement / maxVal) * plotH;
                return (
                  <>
                    <line
                      x1={retirementX} y1={padT} x2={retirementX} y2={padT + plotH}
                      stroke={darkMode ? 'rgba(251,191,36,0.4)' : 'rgba(217,119,6,0.3)'}
                      strokeWidth="1" strokeDasharray="3 3"
                    />
                    <circle cx={retirementX} cy={dotY} r="4"
                      fill={darkMode ? '#fbbf24' : '#d97706'}
                      stroke={darkMode ? '#1f2937' : '#ffffff'} strokeWidth="2"
                    />
                    <text x={retirementX} y={padT - (isMobile ? 6 : 4)} textAnchor="middle"
                      fill={darkMode ? '#fbbf24' : '#d97706'} fontSize={retireLabelFontSize} fontWeight="600"
                    >
                      Retire {retirementYear}
                    </text>
                  </>
                );
              })()}

              {xTicks.map(t => (
                <g key={t.year}>
                  <line
                    x1={t.x} y1={padT + plotH} x2={t.x} y2={padT + plotH + 4}
                    stroke={darkMode ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)'}
                  />
                  <text x={t.x} y={padT + plotH + (isMobile ? 22 : 16)} textAnchor="middle" fill={darkMode ? '#6b7280' : '#9ca3af'} fontSize={labelFontSize}>
                    {t.year}
                  </text>
                </g>
              ))}
            </svg>
            {horizonTruncated && (
              <div className={`mt-2 px-2 flex flex-wrap items-center justify-between gap-2 text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                <span>
                  Your trajectory through <strong className={darkMode ? 'text-slate-200' : 'text-slate-700'}>{lastVisibleYear}</strong>.
                  {' '}See all {rows.length} years with Pro.
                </span>
                {onUpgradeClick && (
                  <button
                    onClick={onUpgradeClick}
                    className="font-semibold text-violet-500 hover:text-violet-400 transition-colors"
                  >
                    Upgrade
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Holdings & Growth Rates — collapsible */}
        <div className={`rounded-xl ${cardBg} overflow-hidden`}>
          <button
            onClick={() => setHoldingsOpen(prev => !prev)}
            className={`${sectionToggleClass} px-4`}
          >
            {holdingsOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            Holdings & Growth Rates
            <span className={`text-[10px] font-normal ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
              {holdings.length} asset{holdings.length !== 1 ? 's' : ''}
            </span>
          </button>
          {holdingsOpen && (
            <div className="px-4 pb-4 space-y-4">
              {(['stocks', 'crypto', '401k', 'pe', 'cash'] as AssetClass[]).map(ac => {
                const group = consolidatedByClass[ac];
                if (!group || group.length === 0) return null;
                return (
                  <div key={ac}>
                    <p className={`text-[10px] font-semibold uppercase tracking-wider mb-2 ${
                      darkMode ? 'text-slate-500' : 'text-slate-400'
                    }`}>
                      {groupLabels[ac]}
                    </p>
                    <div className="space-y-1.5">
                      {group.map(h => (
                        <div
                          key={h.keys[0]}
                          className={`flex items-center justify-between gap-3 px-3 py-2 rounded-lg ${
                            darkMode ? 'bg-white/5' : 'bg-slate-100'
                          }`}
                        >
                          <div className="flex-1 min-w-0">
                            <span className={`text-sm font-medium ${darkMode ? 'text-slate-200' : 'text-slate-800'}`}>
                              {h.name}
                            </span>
                            <span className={`text-xs ml-2 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                              {privacyMode ? '••••' : formatCurrency(h.totalValue)}
                            </span>
                          </div>
                          <div className="flex items-center gap-0.5 flex-shrink-0">
                            <button
                              onClick={() => {
                                const current = Number(cagrOverrides[h.keys[0]] ?? h.cagr) || 0;
                                handleConsolidatedCagrChange(h.keys, String(Math.max(current - 1, -50)));
                              }}
                              className={`w-6 h-6 rounded flex items-center justify-center text-xs font-bold transition-colors ${
                                darkMode
                                  ? 'bg-white/10 text-slate-400 hover:bg-white/20 hover:text-white'
                                  : 'bg-slate-200 text-slate-500 hover:bg-slate-300 hover:text-slate-700'
                              }`}
                            >
                              &minus;
                            </button>
                            <input
                              type="text"
                              inputMode="decimal"
                              value={cagrOverrides[h.keys[0]] ?? String(h.cagr)}
                              onChange={e => handleConsolidatedCagrChange(h.keys, e.target.value.replace(/[^0-9.-]/g, ''))}
                              onFocus={e => e.target.select()}
                              className={`w-12 px-1 py-1 rounded text-center text-xs font-medium ${
                                darkMode
                                  ? 'bg-white/10 border border-white/10 text-violet-400'
                                  : 'bg-violet-50 border border-violet-200 text-violet-600'
                              }`}
                            />
                            <button
                              onClick={() => {
                                const current = Number(cagrOverrides[h.keys[0]] ?? h.cagr) || 0;
                                handleConsolidatedCagrChange(h.keys, String(current + 1));
                              }}
                              className={`w-6 h-6 rounded flex items-center justify-center text-xs font-bold transition-colors ${
                                darkMode
                                  ? 'bg-white/10 text-slate-400 hover:bg-white/20 hover:text-white'
                                  : 'bg-slate-200 text-slate-500 hover:bg-slate-300 hover:text-slate-700'
                              }`}
                            >
                              +
                            </button>
                            <span className={`text-[10px] ml-0.5 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>%</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
              <p className={`text-[10px] ${darkMode ? 'text-slate-600' : 'text-slate-400'}`}>
                Set your estimated annual growth rate (CAGR) per asset. Use the +/&minus; buttons to adjust.
              </p>
            </div>
          )}
        </div>

        {/* Year-by-Year Breakdown — Pro. Free gets the headline numbers and a
            short horizon above; the full table is the depth being sold. */}
        {rows.length > 0 && !isPro && (
          <div className={`rounded-xl ${cardBg} px-4 py-4 flex flex-wrap items-center justify-between gap-3`}>
            <div>
              <p className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                Year-by-Year Breakdown
              </p>
              <p className={`text-xs mt-0.5 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                Opening balance, gains and withdrawals for all {rows.length} years.
              </p>
            </div>
            {onUpgradeClick && (
              <button
                onClick={onUpgradeClick}
                className="px-4 py-2 rounded-lg text-sm font-semibold bg-violet-600 hover:bg-violet-500 text-white transition-colors"
              >
                Unlock with Pro
              </button>
            )}
          </div>
        )}

        {rows.length > 0 && isPro && (
          <div className={`rounded-xl ${cardBg} overflow-hidden`}>
            <button
              onClick={() => setBreakdownOpen(prev => !prev)}
              className={`${sectionToggleClass} px-4`}
            >
              {breakdownOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              Year-by-Year Breakdown
              <span className={`text-[10px] font-normal ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                {rows.length} years
              </span>
            </button>
            {breakdownOpen && (
              <div className="px-4 pb-4 overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className={darkMode ? 'text-slate-500' : 'text-slate-400'}>
                      <th className="text-left py-2 font-medium">Year</th>
                      <th className="text-right py-2 font-medium">Start</th>
                      <th className="text-right py-2 font-medium">Gains</th>
                      <th className="text-right py-2 font-medium">Withdrawals</th>
                      <th className="text-right py-2 font-medium">End</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => {
                      const isRetireYear = r.year === retirementYear;
                      return (
                        <tr
                          key={r.year}
                          className={`border-t ${
                            isRetireYear
                              ? (darkMode ? 'border-amber-500/30 bg-amber-500/5' : 'border-amber-400/30 bg-amber-50')
                              : (darkMode ? 'border-white/5' : 'border-slate-100')
                          }`}
                        >
                          <td className={`py-1.5 font-medium ${
                            isRetireYear
                              ? (darkMode ? 'text-amber-400' : 'text-amber-600')
                              : (darkMode ? 'text-slate-300' : 'text-slate-700')
                          }`}>
                            {r.year}{isRetireYear ? ' *' : ''}
                          </td>
                          <td className={`text-right py-1.5 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                            {privacyMode ? '••••' : formatCompact(r.openingBalance)}
                          </td>
                          <td className={`text-right py-1.5 ${darkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>
                            {privacyMode ? '••••' : `+${formatCompact(r.appreciation)}`}
                          </td>
                          <td className={`text-right py-1.5 ${
                            r.spending > 0
                              ? (darkMode ? 'text-red-400' : 'text-red-600')
                              : (darkMode ? 'text-slate-600' : 'text-slate-300')
                          }`}>
                            {privacyMode ? '••••' : r.spending > 0 ? `-${formatCompact(r.spending)}` : '$0'}
                          </td>
                          <td className={`text-right py-1.5 font-medium ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                            {privacyMode ? '••••' : formatCompact(r.closingBalance)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <p className={`text-[10px] mt-3 ${darkMode ? 'text-slate-600' : 'text-slate-400'}`}>
                  * marks your target retirement year. Withdrawals begin then, adjusted for {inflationRate}% inflation annually{taxRate > 0 ? ` and grossed up for your ${taxRate}% tax rate` : ''}.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Financial Disclaimer */}
        <div className={`flex items-start gap-2.5 rounded-xl p-3 mt-4 text-xs ${
          darkMode
            ? 'bg-white/5 border border-white/10 text-slate-500'
            : 'bg-slate-50 border border-slate-200 text-slate-400'
        }`}>
          <Info size={14} className="flex-shrink-0 mt-0.5" />
          <p>
            Hypothetical projections based on user inputs. Projections are estimates for informational purposes only and do not constitute financial,
            investment, tax, or legal advice. Actual results may vary significantly. Consult a qualified
            financial advisor before making investment decisions.
          </p>
        </div>

      </div>
    </div>
  );
};
